import type {
  ControlNode as ControlNodeType,
  Port,
} from "../../../../util/src/blueprint/types.js";
import { PortHandle } from "../PortHandle.js";
import dico from "../../../../util/src/language/dico.js";
import React from "react";

export interface ControlIfElseNodeProps {
  node: ControlNodeType;
  setPortsMap: (
    nodeId: string,
    portId: string,
    element: HTMLElement | null,
  ) => void;
  onPortMouseDown?: (nodeId: string, port: Port) => void;
  onPortMouseUp?: (nodeId: string, port: Port) => void;
  isPortConnected?: (nodeId: string, portId: string) => boolean;
  onPortValueChange?: (nodeId: string, portId: string, value: unknown) => void;
}

const ControlIfElseNodeComponent = ({
  node,
  setPortsMap,
  onPortMouseDown,
  onPortMouseUp,
  isPortConnected,
  onPortValueChange,
}: ControlIfElseNodeProps) => {
  const behaviorDico = dico().behavior;
  const conditionPort = node.ports.find((port) => port.id === "condition");
  const valuePort = node.ports.find((port) => port.id === "value");
  const truePort = node.ports.find((port) => port.id === "true");
  const falsePort = node.ports.find((port) => port.id === "false");
  if (!conditionPort || !valuePort || !truePort || !falsePort) return null;

  return (
    <div
      style={{
        minWidth: 260,
        borderRadius: 8,
        overflow: "hidden",
        background:
          "linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(200, 200, 200, 0.98) 100%)",
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        border: "1px solid #e5e7eb",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          padding: "4px 8px",
          fontSize: 12,
          fontWeight: 700,
          color: "#fff",
          background:
            "linear-gradient(180deg, #93c5fd 0%, #3b82f6 55%, #1d4ed8 100%)",
        }}
      >
        {behaviorDico.controls.ifElse}
      </div>
      <div style={{ display: "flex", alignItems: "stretch", padding: "6px 0" }}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            flex: 1,
          }}
        >
          <PortHandle
            node={node}
            port={valuePort}
            setPortsMap={setPortsMap}
            onMouseDown={() => onPortMouseDown?.(node.id, valuePort)}
            onMouseUp={() => onPortMouseUp?.(node.id, valuePort)}
            isConnected={isPortConnected?.(node.id, valuePort.id) ?? false}
          />
          <PortHandle
            node={node}
            port={conditionPort}
            setPortsMap={setPortsMap}
            onMouseDown={() => onPortMouseDown?.(node.id, conditionPort)}
            onMouseUp={() => onPortMouseUp?.(node.id, conditionPort)}
            showValueInput
            onValueChange={(value) =>
              onPortValueChange?.(node.id, conditionPort.id, value)
            }
            isConnected={isPortConnected?.(node.id, conditionPort.id) ?? false}
          />
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            flex: 1,
          }}
        >
          <PortHandle
            node={node}
            port={truePort}
            setPortsMap={setPortsMap}
            onMouseDown={() => onPortMouseDown?.(node.id, truePort)}
            onMouseUp={() => onPortMouseUp?.(node.id, truePort)}
            isConnected={isPortConnected?.(node.id, truePort.id) ?? false}
          />
          <PortHandle
            node={node}
            port={falsePort}
            setPortsMap={setPortsMap}
            onMouseDown={() => onPortMouseDown?.(node.id, falsePort)}
            onMouseUp={() => onPortMouseUp?.(node.id, falsePort)}
            isConnected={isPortConnected?.(node.id, falsePort.id) ?? false}
          />
        </div>
      </div>
    </div>
  );
};

export const ControlIfElseNode = React.memo(ControlIfElseNodeComponent);
